<div class="container-fluid py-3" *ngIf="slide as s">
  <div class="card mb-3">
    <div class="card-body">
      <div class="row g-3 slide-header-info">
        <div class="col-sm-6 col-md-3 col-lg-2">
          <label class="form-label">Project ID</label>
          <input class="form-control" [value]="s.projectId" readonly>
        </div>
        <div class="col-sm-6 col-md-3 col-lg-2">
          <label class="form-label">Slide ID</label>
          <input class="form-control" [value]="s.slideId" readonly>
        </div>
        <div class="col-sm-12 col-md-6 col-lg-4">
          <label class="form-label">Slide title</label>
          <input class="form-control" [value]="s.slideTitle || '—'" readonly>
        </div>
        <div class="col-sm-6 col-md-4 col-lg-2">
          <label class="form-label">Slide type</label>
          <input class="form-control" [value]="s.slideType" readonly>
        </div>
      </div>
      <div class="slide-header-actions d-flex flex-wrap align-items-center gap-2 mt-3 pt-3 border-top">
        <button class="btn btn-outline-primary" type="button" (click)="updateAllLines()">
          <i class="bi bi-save-fill"></i> Update
        </button>
        <select class="form-select form-select-sm slide-lang-select"
          [ngModel]="currentLanguageCode" (ngModelChange)="onCurrentLanguageChange($event)"
          [disabled]="translating || generatingTranslatedAudio"
          title="Current language for this project (session; used across slides and transfer export)">
          <option value="">EN — English (source)</option>
          <option *ngFor="let lang of languages" [value]="lang.LanguageShortCode">
            {{ lang.LanguageShortCode }} — {{ lang.LanguageName }}
          </option>
        </select>
        <button class="btn btn-outline-secondary" type="button" (click)="translateSlideForLanguage()"
          [disabled]="translating || generatingAudio || generatingTranslatedAudio || clearReseeding || pickingImages || !languages.length"
          [title]="!languages.length ? 'Add languages in Admin → Manage Languages first.' : (!translationModeActive ? 'Choose a translation language from the dropdown (not EN).' : '')">
          {{ translating ? 'Translating…' : 'Translate slide' }}
        </button>
        <button class="btn btn-outline-secondary" type="button" (click)="generateTranslatedAudioForSlide()"
          [disabled]="generatingTranslatedAudio || generatingAudio || translating || clearReseeding || pickingImages || !translationModeActive || !hasAudioLines || !languages.length"
          [title]="!languages.length ? 'Add languages in Admin first.' : (!translationModeActive ? 'Choose a translation language from the dropdown.' : (!hasAudioLines ? 'No audio lines on this slide.' : ''))">
          {{ generatingTranslatedAudio ? 'Gen. trans. audio…' : 'Generate translated audio' }}
        </button>
        <button class="btn btn-outline-success" type="button" (click)="generateAudioForSlide()"
          [disabled]="generatingAudio || clearReseeding || pickingImages || translating || generatingTranslatedAudio || !hasAudioLines"
          title="English source audio (default language option)">
          {{
            generatingAudio
              ? 'Generating audio…'
              : (!hasAudioLines ? 'No audio lines' : (audioAlreadyGenerated ? 'Regenerate audio' : 'Generate audio'))
          }}
        </button>
        <button class="btn btn-outline-info" type="button" (click)="pickLessonImagesFromLesson()"
          [disabled]="pickingImages || clearReseeding || generatingAudio || !hasImageLines || !lessonLinked"
          [title]="!lessonLinked ? 'Link a lesson to this project first.' : (!hasImageLines ? 'No IMAGE lines on this slide.' : '')">
          {{ pickingImages ? 'Picking images…' : 'Pick images from lesson' }}
        </button>
        <button class="btn btn-outline-warning" type="button" (click)="clearAndReseedFromSlideType()"
          [disabled]="clearReseeding || generatingAudio || pickingImages || finalizeLayouting">
          {{ clearReseeding ? 'Clear & seed…' : 'Clear & seed lines' }}
        </button>
        <button class="btn btn-outline-info" type="button" (click)="applyGroupLayout()"
          [disabled]="finalizeLayouting || clearReseeding || generatingAudio || pickingImages"
          title="Recompute object-group positions after adding, removing, or reordering groups">
          {{ finalizeLayouting ? 'Applying layout…' : 'Apply group layout' }}
        </button>
        <button class="btn btn-outline-secondary" type="button" (click)="openAddGroupAtEnd()"
          [disabled]="clearReseeding || generatingAudio || pickingImages || finalizeLayouting || !availableGroups.length"
          [title]="!availableGroups.length ? 'No object groups defined for this slide type.' : 'Add object group after last line'">
          <i class="bi bi-folder-plus"></i> Add Group
        </button>
      </div>
    </div>

    <p class="text-muted small mb-2" *ngIf="showEntry || showExit">{{ timelineDirectiveHelp }}</p>

    <div class="d-flex gap-2 mb-2">
      <div class="form-check form-switch">
        <input class="form-check-input" type="checkbox" id="entryToggle" [(ngModel)]="showEntry">
        <label class="form-check-label" for="entryToggle">Show Entry Animation</label>
      </div>
      <div class="form-check form-switch">
        <input class="form-check-input" type="checkbox" id="exitToggle" [(ngModel)]="showExit">
        <label class="form-check-label" for="exitToggle">Show Exit Animation</label>
      </div>
      <div class="form-check form-switch">
        <input class="form-check-input" type="checkbox" id="miscToggle" [(ngModel)]="showMisc">
        <label class="form-check-label" for="miscToggle">Show Misc</label>
      </div>
    </div>

    <datalist [id]="timelineDirectiveDatalistId">
      <option *ngFor="let v of timelineDirectiveDatalistValues" [value]="v"></option>
    </datalist>

    <div class="table-responsive">
      <table class="table table-sm align-middle table-bordered">
        <thead class="table-light">
          <tr>
            <th style="width: 8rem;" class="text-center">Up/Dn/Add/Del</th>
            <th *ngFor="let col of mainColumns">{{ col.label }}</th>
            <th *ngIf="showTranslatedColumn">{{ translatedColumnLabel }}</th>
            <ng-container *ngIf="showEntry">
              <th *ngFor="let col of entryColumns">{{ col.label }}</th>
            </ng-container>
            <ng-container *ngIf="showExit">
              <th *ngFor="let col of exitColumns">{{ col.label }}</th>
            </ng-container>
            <ng-container *ngIf="showMisc">
              <th *ngFor="let col of miscColumns">{{ col.label }}</th>
            </ng-container>
          </tr>
        </thead>
        <tbody>
          <ng-container *ngFor="let item of flatten(s.lines); trackBy: trackById">
            <tr>
              <!-- ACTIONS -->
              <td class="text-center">
                <div class="btn-group btn-group-sm">
                  <button class="btn btn-outline-secondary"
                    [disabled]="!canMoveUp(item.node, item.level === 0 ? null : null)"
                    (click)="move(item.node, item.level === 0 ? null : null, -1)" title="Move up">
                    <i class="bi bi-arrow-up"></i>
                  </button>
                  <button class="btn btn-outline-secondary"
                    [disabled]="!canMoveDown(item.node, item.level === 0 ? null : null)"
                    (click)="move(item.node, item.level === 0 ? null : null, 1)" title="Move down">
                    <i class="bi bi-arrow-down"></i>
                  </button>
                  <button class="btn btn-outline-secondary"
                    [disabled]="!canInsertAfter(item.node, item.level === 0 ? null : null)"
                    (click)="prepareInsert(item.node)" title="Add group after">
                    <i class="bi bi-plus"></i>
                  </button>
                  <button *ngIf="item.node.main?.Type?.toUpperCase() === 'AUDIO'"
                    class="btn btn-outline-warning"
                    [disabled]="regeneratingLineId===item.node.id"
                    (click)="regenerateAudioForLine(item.node)"
                    title="Regenerate audio for this line">
                    {{ regeneratingLineId===item.node.id ? '…' : 'Regen' }}
                  </button>
                  <button class="btn btn-outline-danger" (click)="delete(item.node, item.level === 0 ? null : null)"
                    title="Delete">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </td>
              <!-- MAIN GROUP -->
              <!-- Main Columns -->
              <td *ngFor="let col of mainColumns">
                <ng-container [ngSwitch]="col.key">
                  <div *ngSwitchCase="'Name'" class="d-flex align-items-center"
                    [style.paddingLeft.px]="item.level * 16">
                    <button *ngIf="item.node.kind==='OG'" class="btn btn-sm btn-outline-secondary me-2"
                      (click)="toggleGroup(item.node)">
                      <i class="bi" [ngClass]="item.node.isOpen ? 'bi-caret-down-fill' : 'bi-caret-right-fill'"></i>
                    </button>
                    <span>{{ item.node.PSLrawName }}</span>
                  </div>
                  <div *ngSwitchCase="'Value'" class="text-cell-editor">
                    <div class="text-preview" [title]="item.node.main.Value">{{ previewText(item.node.main.Value) }}</div>
                    <button type="button" class="btn btn-sm btn-outline-secondary text-expand-btn"
                      (click)="openTextEditor('value', item.node)" title="Edit full value">
                      <i class="bi bi-arrows-angle-expand"></i>
                    </button>
                  </div>
                  <input *ngSwitchDefault [type]="col.type || 'text'" class="form-control form-control-sm"
                    [(ngModel)]="item.node.main[col.key]">
                </ng-container>
              </td>

              <td *ngIf="showTranslatedColumn">
                <ng-container *ngIf="hasTranslatedCell(item.node); else noTranslationCell">
                  <div class="text-cell-editor">
                    <div class="text-preview" [title]="item.node.translatedText">{{ previewText(item.node.translatedText) }}</div>
                    <button type="button" class="btn btn-sm btn-outline-secondary text-expand-btn"
                      (click)="openTextEditor('translated', item.node)" title="Edit full translated text">
                      <i class="bi bi-arrows-angle-expand"></i>
                    </button>
                  </div>
                </ng-container>
                <ng-template #noTranslationCell>
                  <span class="text-muted small">—</span>
                </ng-template>
              </td>

              <!-- Entry -->
              <ng-container *ngIf="showEntry">
                <td *ngFor="let col of entryColumns">
                  <ng-container [ngSwitch]="col.type">
                    <input *ngSwitchCase="'timelineDirective'" type="text" class="form-control form-control-sm"
                      maxlength="100" [attr.list]="timelineDirectiveDatalistId"
                      [title]="timelineDirectiveHelp"
                      [(ngModel)]="item.node.entry[col.key]" autocomplete="off" spellcheck="false">
                    <input *ngSwitchDefault [type]="col.type || 'text'" class="form-control form-control-sm"
                      [(ngModel)]="item.node.entry[col.key]">
                  </ng-container>
                </td>
              </ng-container>

              <!-- Exit -->
              <ng-container *ngIf="showExit">
                <td *ngFor="let col of exitColumns">
                  <ng-container [ngSwitch]="col.type">
                    <input *ngSwitchCase="'timelineDirective'" type="text" class="form-control form-control-sm"
                      maxlength="100" [attr.list]="timelineDirectiveDatalistId"
                      [title]="timelineDirectiveHelp"
                      [(ngModel)]="item.node.exit[col.key]" autocomplete="off" spellcheck="false">
                    <input *ngSwitchDefault [type]="col.type || 'text'" class="form-control form-control-sm"
                      [(ngModel)]="item.node.exit[col.key]">
                  </ng-container>
                </td>

              </ng-container>
              <!-- Misc -->
              <ng-container *ngIf="showMisc">
                <td *ngFor="let col of miscColumns">
                  <ng-container [ngSwitch]="col.key">
                    <select *ngSwitchCase="'AudioOptions'" class="form-select form-select-sm"
                      [(ngModel)]="item.node.misc[col.key]">
                      <option value="">—</option>
                      <option *ngFor="let k of audioOptionKeys" [value]="k">{{ k }}</option>
                    </select>
                    <div *ngSwitchCase="'AudioText'" class="text-cell-editor">
                      <div class="text-preview" [title]="item.node.misc.AudioText">{{ previewText(item.node.misc.AudioText) }}</div>
                      <button type="button" class="btn btn-sm btn-outline-secondary text-expand-btn"
                        (click)="openTextEditor('audioText', item.node)" title="Edit full audio script">
                        <i class="bi bi-arrows-angle-expand"></i>
                      </button>
                    </div>
                    <input *ngSwitchDefault [type]="col.type || 'text'" class="form-control form-control-sm"
                      [(ngModel)]="item.node.misc[col.key]">
                  </ng-container>
                </td>
              </ng-container>
            </tr>
          </ng-container>
        </tbody>
      </table>
    </div>
  </div>

  <div *ngIf="loading" class="text-center py-5">
    <div class="spinner-border" role="status"></div>
    <div>Loading slide…</div>
  </div>
  <div class="modal fade" id="textEditModal" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ textEditTitle || 'Edit text' }}</h5>
          <button type="button" class="btn-close" (click)="cancelTextEditor()"></button>
        </div>
        <div class="modal-body">
          <textarea class="form-control text-edit-area" rows="14" [(ngModel)]="textEditDraft"
            spellcheck="true"></textarea>
          <p class="form-text mb-0 mt-2">{{ textEditDraft.length }} characters</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="cancelTextEditor()">Cancel</button>
          <button type="button" class="btn btn-primary" (click)="applyTextEditor()">Apply</button>
        </div>
      </div>
    </div>
  </div>

  <div class="modal fade" id="insertGroupModal" tabindex="-1">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Select Group</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <select class="form-select" [(ngModel)]="selectedGroup">
            <option [ngValue]="null">— Select group —</option>
            <option *ngFor="let g of availableGroups" [value]="g.groupname">{{ g.groupname }}</option>
          </select>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button class="btn btn-primary" (click)="confirmInsert()" data-bs-dismiss="modal">
            Insert
          </button>
        </div>
      </div>
    </div>
  </div>